import * as React from 'react';

// className joiner
const cx = (...parts: (string | undefined | false)[]) =>
  parts.filter(Boolean).join(' ');

type IdProps = {
  children: React.ReactNode;
  id?: string;
  className?: string;
};

type DivProps = IdProps & React.HTMLAttributes<HTMLDivElement>;
type H1Props = IdProps & React.HTMLAttributes<HTMLHeadingElement>;

export const ClaimsContainer = ({
  children,
  id,
  className,
  ...rest
}: DivProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className={[
        'tw-bg-unm-container-bg tw-flex tw-flex-col tw-mx-auto tw-max-w-[904px]',
        'tw-[&_p]:m-0',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
};

export const Header = ({ children, id, className, ...rest }: H1Props) => {
  return (
    <h1
      {...(id ? { id } : {})}
      className={[
        'tw-text-unm-interactive01-800 tw-font-ubuntu tw-font-normal',
        'tw-text-3xl sm:tw-text-4xl tw-leading-[48px]',
        'tw-mb-6 tw-text-left tw-w-full',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </h1>
  );
};

export const ButtonGroupContainer = ({
  children,
  id,
  className,
  ...rest
}: DivProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className={['tw-flex tw-justify-end tw-mt-4', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
};

export type Props = {
  children: React.ReactNode;
  id?: string;
  className?: string;
};

type ContainerBaseProps = {
  id: string;
};

type ContainerProps = ContainerBaseProps & React.HTMLAttributes<HTMLDivElement>;

export const ClaimCard = ({
  children,
  id,
  className,
  role,
  ...rest
}: ContainerProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      {...(role ? { role } : {})}
      className={cx(
        'tw-bg-white tw-rounded-[8px] tw-shadow-[0_3px_6px_rgba(46,46,46,0.16)] tw-max-w-[904px]',
        'tw-overflow-hidden tw-border-solid tw-border-l-[16px] tw-border-unm-interactive01-400',
        'tw-border-0 hover:tw-bg-[#E6EEF4] tw-cursor-pointer tw-flex tw-flex-col',
        'tw-px-6 tw-pt-5 tw-pb-2 tw-mb-4',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

/** =========================================================
 *  TitleBar — Row aligning icon + header
 *==========================================================*/
export const TitleBar = ({ children, id, className }: IdProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className={cx('tw-flex tw-flex-row tw-items-center', className)}
    >
      {children}
    </div>
  );
};

/** =========================================================
 *  Header — Main Card Title
 *==========================================================*/

export const CardHeader = ({ children, id, className }: IdProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className={cx(
        'tw-text-unm-primary01-700 tw-font-sans tw-font-normal tw-text-2xl sm:tw-text-[28px] tw-leading-8',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const Description = ({ children, id, className }: IdProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className={cx(
        'tw-text-unm-secondary01-500 tw-font-sans tw-font-normal  tw-text-base lg:tw-text-lg tw-leading-6',
        'tw-mt-3 tw-mb-0',
        className,
      )}
    >
      {children}
    </div>
  );
};

/** =========================================================
 *  MediumHeader — Secondary Title (Not Found state)
 *==========================================================*/
export const MediumHeader = ({ children, id, className }: IdProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className={cx(
        'tw-text-unm-primary02-400 tw-font-ubuntu tw-font-normal tw-text-[20px] tw-leading-8 tw-m-0',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const NotFoundContainer = ({ children, id, className }: IdProps) => {
  return (
    <div
      {...(id ? { id } : {})}
      className={cx(
        'tw-rounded-[10px] tw-p-8 tw-w-full tw-shadow-[0_3px_6px_rgba(46,46,46,0.16)]',
        'tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-max-w-[904px] tw-min-h-[259px]',
        'tw-bg-[#eff3f8] tw-border tw-border-unm-required-bg tw-mb-4',
        className,
      )}
    >
      {children}
    </div>
  );
};
